// ============================================================================
// ANALYTICS THEME & COLOR SYSTEM
// ============================================================================
// Design tokens (brioColors, brioStyles), the BRIO project color/palette system,
// role colors, and a small linear-regression helper. Extracted from
// AnalyticsDashboardPage so sections and shared components can import them.
// Pure module-level values — no React/component-scope dependencies.
// ============================================================================

// Role -> colour, mirroring AdminTeamOverviewPage's ROLE_COLORS (the --brio-role-*
// tokens in brio-styles.css). Keyed case-insensitively so role-history text such
// as "Senior Architect" matches regardless of casing. Unmapped roles fall back
// to the same beige the team overview uses.
const ROLE_COLOR_HEX = {
  'director': '#a97e53',
  'partner': '#a97e53',
  'associate': '#49685a',
  'senior architect': '#689581',
  'project architect': '#7f7322',
  'medior architect': '#b2a655',
  'junior architect': '#d9d3aa',
  'intern': '#f0b596',
  'support': '#d3b1ab',
  'model maker': '#e6c88a',
  'visualiser': '#c9a0b8',
  'others': '#b0b0b0',
};
const ROLE_FALLBACK_COLOR = '#D4C4A0';
const roleColorFor = (role) => ROLE_COLOR_HEX[String(role || '').trim().toLowerCase()] || ROLE_FALLBACK_COLOR;

// Ordinary-least-squares fit for a set of {x, y} points. Returns the slope,
// intercept and R² (coefficient of determination) so a trend's direction and
// strength can be reported numerically. Null when there aren't enough points.
function linearRegression(points) {
  const n = points.length;
  if (n < 2) return { slope: null, intercept: null, r2: null };
  let sx = 0, sy = 0;
  for (const p of points) { sx += p.x; sy += p.y; }
  const mx = sx / n, my = sy / n;
  let sxx = 0, sxy = 0, syy = 0;
  for (const p of points) {
    const dx = p.x - mx, dy = p.y - my;
    sxx += dx * dx; sxy += dx * dy; syy += dy * dy;
  }
  if (sxx === 0) return { slope: null, intercept: null, r2: null };
  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  const r2 = syy === 0 ? 1 : (sxy * sxy) / (sxx * syy);
  return { slope, intercept, r2 };
}

const brioColors = {
  gold: '#9F902A',
  goldHover: '#7F7321',
  goldLight: 'rgba(159, 144, 42, 0.1)',
  goldMedium: 'rgba(159, 144, 42, 0.3)',
  white: '#ffffff',
  black: '#111111',
  grey: '#666666',
  greyLight: '#f5f5f5',
  border: 'rgba(0, 0, 0, 0.1)',
};

const brioStyles = {
  // Typography
  fontFamily: 'Raleway, sans-serif',
  fontSizeBase: '13px',
  fontSizeH2: '15px',
  fontWeightLight: 200,
  fontWeightRegular: 300,
  fontWeightMedium: 400,
  lineHeight: 1.5,
  
  // Spacing
  spacingXS: '8px',
  spacingS: '16px',
  spacingM: '24px',
  spacingL: '32px',
  spacingXL: '48px',
  
  // Layout
  maxWidthContent: '540px',
  maxWidthContainer: '970px',
};

// Global CSS injection removed

// ============================================================================
// BRIO PROJECT COLOR SYSTEM (from CSS variables)
// ============================================================================

/**
 * Parse a CSS variable containing comma-separated hex colors into an array
 * @param {string} varName - CSS variable name (e.g., '--brio-hospitality-palette')
 * @returns {string[]} - Array of hex color strings
 */
const parseCssPalette = (varName) => {
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (!value) return [];
    return value.split(',').map(c => c.trim()).filter(c => c.startsWith('#'));
  } catch {
    return [];
  }
};

// Memoized palette cache to avoid repeated DOM reads
let _paletteCache = null;
const getBrioPalettes = () => {
  if (_paletteCache) return _paletteCache;
  
  _paletteCache = {
    cultural: parseCssPalette('--brio-cultural-palette'),
    educational: parseCssPalette('--brio-educational-palette'),
    houses: parseCssPalette('--brio-houses-palette'),
    housing: parseCssPalette('--brio-housing-palette'),
    hospitality: parseCssPalette('--brio-hospitality-palette'),
    institutional: parseCssPalette('--brio-institutional-palette'),
    interior: parseCssPalette('--brio-interior-palette'),
    ngo: parseCssPalette('--brio-ngo-palette'),
    public: parseCssPalette('--brio-public-palette'),
    brio: parseCssPalette('--brio-brio-palette'),
  };
  
  // Fallback palettes if CSS variables aren't loaded
  const fallbacks = {
    cultural: ['#DB6B5D', '#E28069', '#A66257', '#D3967A', '#994b41', '#af564a', '#c56054', '#db6b5d'],
    educational: ['#F1B476', '#5f7d6e', '#779c8a', '#92b0a1', '#adc4b9', '#c9d7d0', '#6a8370', '#84a48c'],
    houses: ['#ECD288', '#a5935f', '#bda86d', '#f0dba0', '#f4e4b8', '#f7edcf', '#f0d794', '#c0ac76'],
    housing: ['#c59b76', '#f6c293', '#f8cea9', '#fadabe', '#fbe7d4'],
    hospitality: ['#c0886e', '#f0aa89', '#f3bba1', '#f6ccb8', '#f9ddd0', '#be825e', '#eda376', '#f2bf9f'],
    institutional: ['#854e46', '#a66257', '#b88179', '#caa19a', '#dbc0bc', '#763a3a', '#934948', '#a96d6d'],
    interior: ['#c39868', '#f4be82', '#f6cb9b', '#f8d8b4', '#fbe5cd'],
    ngo: ['#537767', '#689581', '#86aa9a', '#a4bfb3', '#c3d5cd'],
    public: ['#969e78', '#bcc596', '#c9d1ab', '#d7dcc0', '#e4e8d5', '#949681', '#b9bca1', '#c7c9b4'],
    brio: ['#714035', '#5a332a', '#53312c', '#7a5f41', '#77523b'],
  };
  
  // Apply fallbacks for any empty palettes
  Object.keys(fallbacks).forEach(key => {
    if (!_paletteCache[key] || _paletteCache[key].length === 0) {
      _paletteCache[key] = fallbacks[key];
    }
  });
  
  return _paletteCache;
};

// Map category names to palette keys (handles aliases)
const categoryToPaletteKey = (category) => {
  const categoryMap = {
    cultural: 'cultural',
    educational: 'educational',
    houses: 'houses',
    housing: 'housing',
    hospitality: 'hospitality',
    institutional: 'institutional',
    interior: 'interior',
    ngo: 'ngo',
    public: 'public',
    brio: 'brio',
    residential: 'houses',
    commercial: 'hospitality',
    uncategorized: null,
    other: null,
  };
  return categoryMap[(category || '').toLowerCase()] || null;
};

/**
 * Get primary color for each category (first color in palette)
 * @returns {Object} - Map of category names to primary colors
 */
const getCategoryPrimaryColors = () => {
  const palettes = getBrioPalettes();
  return {
    cultural: palettes.cultural[0] || '#DB6B5D',
    educational: palettes.educational[0] || '#F1B476',
    houses: palettes.houses[0] || '#ECD288',
    housing: palettes.housing[0] || '#c59b76',
    hospitality: palettes.hospitality[0] || '#c0886e',
    institutional: palettes.institutional[0] || '#854e46',
    interior: palettes.interior[0] || '#c39868',
    ngo: palettes.ngo[0] || '#537767',
    public: palettes.public[0] || '#969e78',
    brio: palettes.brio[0] || '#714035',
    uncategorized: brioColors.grey,
    residential: palettes.houses[0] || '#ECD288',
    commercial: palettes.hospitality[0] || '#c0886e',
    other: brioColors.grey,
  };
};

/**
 * Get a deterministic color from a category palette based on identifier
 * @param {string} category - Category name
 * @param {number|string} identifier - Project ID or index for deterministic selection
 * @returns {string} - Hex color string
 */
const getCategoryColorByIndex = (category, identifier) => {
  const palettes = getBrioPalettes();
  const paletteKey = categoryToPaletteKey(category);
  
  if (!paletteKey || !palettes[paletteKey] || palettes[paletteKey].length === 0) {
    return brioColors.grey;
  }
  
  const palette = palettes[paletteKey];
  
  // Deterministic index based on identifier
  let idx = 0;
  if (typeof identifier === 'number') {
    idx = Math.abs(identifier) % palette.length;
  } else if (typeof identifier === 'string') {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = ((hash << 5) - hash) + identifier.charCodeAt(i);
      hash = hash & hash;
    }
    idx = Math.abs(hash) % palette.length;
  }
  
  return palette[idx];
};

/**
 * Get color for a project - uses project.color if available, otherwise falls back to category palette
 * @param {Object} project - Project object with optional color and category properties
 * @param {number} [index=0] - Fallback index for palette selection
 * @returns {string} - Hex color string
 */
const getProjectColor = (project, index = 0) => {
  if (project?.color) {
    return project.color;
  }
  const category = (project?.category || 'uncategorized').toLowerCase();
  return getCategoryColorByIndex(category, project?.id || index);
};

/**
 * Get colors for a list of projects
 * @param {Array} projectList - Array of project objects
 * @param {Object} projectsMap - Optional map of project IDs to project objects
 * @returns {string[]} - Array of hex color strings
 */
const getProjectColors = (projectList, projectsMap = {}) => {
  return projectList.map((item, index) => {
    if (typeof item === 'object' && item !== null) {
      return getProjectColor(item, index);
    }
    // If item is an ID, look up in projectsMap
    const project = projectsMap[item];
    return project ? getProjectColor(project, index) : brioColors.grey;
  });
};

/**
 * Get colors for category distribution charts
 * @param {string[]} categories - Array of category names
 * @returns {string[]} - Array of hex color strings
 */
const getCategoryChartColors = (categories) => {
  const primaryColors = getCategoryPrimaryColors();
  return categories.map(cat => {
    const catLower = (cat || 'uncategorized').toLowerCase();
    return primaryColors[catLower] || brioColors.grey;
  });
};

export {
  ROLE_COLOR_HEX, ROLE_FALLBACK_COLOR, roleColorFor, linearRegression,
  brioColors, brioStyles, parseCssPalette, getBrioPalettes, categoryToPaletteKey,
  getCategoryPrimaryColors, getCategoryColorByIndex, getProjectColor, getProjectColors,
  getCategoryChartColors,
};
